import type {
  CachedSpreadsheetCell,
  CachedSpreadsheetSheetResponse,
  IAiSpreadsheetPasteEdit,
  IAiSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import { OPTIMISTIC_MANUAL_ROW_ID_PREFIX } from "./manual-rows";
import { buildOptimisticSpreadsheetCells } from "./sheet-cells";

export const assignCreatedManualRow = (
  data: CachedSpreadsheetSheetResponse | undefined,
  token: string,
  manualRowId: string,
) =>
  data
    ? {
        ...data,
        sheet: {
          ...data.sheet,
          cells: data.sheet.cells.map((cell) =>
            cell.id.startsWith(`${OPTIMISTIC_MANUAL_ROW_ID_PREFIX}${token}-`)
              ? { ...cell, pending_manual_row_id: manualRowId }
              : cell,
          ),
        },
      }
    : data;

export const buildPendingManualRowCell = (
  cell: CachedSpreadsheetCell,
  edit: Omit<IAiSpreadsheetPasteEdit, "cell_id">,
): CachedSpreadsheetCell => ({
  ...buildOptimisticSpreadsheetCells(
    [cell],
    [{ cell_id: cell.id, ...edit }],
  )[0],
  pending_manual_row_value_type: edit.value_type,
  pending_manual_row_baseline: cell.pending_manual_row_baseline ?? {
    raw_input: cell.raw_input,
    kind: cell.kind,
    value_type: cell.value_type,
  },
});

export const restoreManualRowBaselines = (
  data: CachedSpreadsheetSheetResponse,
): CachedSpreadsheetSheetResponse => ({
  ...data,
  sheet: {
    ...data.sheet,
    cells: data.sheet.cells.map((cell) =>
      cell.pending_manual_row_baseline
        ? { ...cell, ...cell.pending_manual_row_baseline }
        : cell,
    ),
  },
});

export const clearSavedManualRowEdits = (
  data: CachedSpreadsheetSheetResponse | undefined,
  savedIds: ReadonlySet<string>,
) =>
  data
    ? {
        ...data,
        sheet: {
          ...data.sheet,
          cells: data.sheet.cells.map((cell) =>
            savedIds.has(cell.id)
              ? {
                  ...cell,
                  pending_manual_row_baseline: undefined,
                  manual_row_save_failed: undefined,
                  pending_manual_row_value_type: undefined,
                }
              : cell,
          ),
        },
      }
    : data;

export const markFailedManualRowEdits = (
  data: CachedSpreadsheetSheetResponse | undefined,
  token: string,
) =>
  data
    ? {
        ...data,
        sheet: {
          ...data.sheet,
          cells: data.sheet.cells.map((cell) => {
            if (
              !(cell.optimistic_cell_id ?? cell.id).startsWith(
                `${OPTIMISTIC_MANUAL_ROW_ID_PREFIX}${token}-`,
              )
            )
              return cell;
            return {
              ...cell,
              ...(cell.pending_manual_row_baseline
                ? { manual_row_save_failed: true }
                : {}),
              ...(cell.id.startsWith(OPTIMISTIC_MANUAL_ROW_ID_PREFIX)
                ? {
                    source_ref: {
                      ...cell.source_ref,
                      editable_while_pending: false,
                    },
                  }
                : {}),
            };
          }),
        },
      }
    : data;

export const preservePendingManualRows = (
  current: CachedSpreadsheetSheetResponse | undefined,
  incoming: IAiSpreadsheetSheetResponse,
): CachedSpreadsheetSheetResponse => {
  if (!current || current.sheet.id !== incoming.sheet.id) return incoming;

  const byId = new Map(current.sheet.cells.map((cell) => [cell.id, cell]));
  const pendingByRow = new Map<string, Map<number, CachedSpreadsheetCell>>();
  const pendingCells = current.sheet.cells.filter(
    (cell) =>
      cell.id.startsWith(OPTIMISTIC_MANUAL_ROW_ID_PREFIX) &&
      (cell.source_ref?.editable_while_pending === true ||
        cell.manual_row_save_failed),
  );
  const incomingManualRows = new Set(
    incoming.sheet.cells.flatMap((cell) =>
      cell.manual_row_id ? [cell.manual_row_id] : [],
    ),
  );
  const hasPendingInsert = pendingCells.some(
    (cell) =>
      cell.pending_manual_row_insert &&
      (!cell.pending_manual_row_id ||
        !incomingManualRows.has(cell.pending_manual_row_id)),
  );
  for (const cell of pendingCells) {
    const manualRowId = cell.pending_manual_row_id;
    if (!manualRowId) continue;
    const columns = pendingByRow.get(manualRowId) ?? new Map();
    columns.set(cell.col, cell);
    pendingByRow.set(manualRowId, columns);
  }
  const matched = new Set<string>();
  const cells = incoming.sheet.cells.map((incomingCell) => {
    const pending = incomingCell.manual_row_id
      ? pendingByRow.get(incomingCell.manual_row_id)?.get(incomingCell.col)
      : undefined;
    const prior = byId.get(incomingCell.id);
    const cached = pending ?? prior;
    const cell =
      hasPendingInsert && cached && cached.row > incomingCell.row
        ? { ...incomingCell, row: cached.row }
        : incomingCell;
    if (pending) {
      matched.add(pending.id);
      return {
        ...cell,
        optimistic_cell_id: pending.id,
        ...(pending.pending_manual_row_baseline &&
        (cell.raw_input !== pending.raw_input ||
          cell.kind !== pending.kind ||
          cell.value_type !== pending.value_type)
          ? {
              pending_manual_row_baseline: {
                raw_input: cell.raw_input,
                kind: cell.kind,
                value_type: cell.value_type,
              },
              raw_input: pending.raw_input,
              computed_value: pending.computed_value,
              kind: pending.kind,
              value_type: pending.value_type,
              manual_row_save_failed: pending.manual_row_save_failed,
              pending_manual_row_value_type:
                pending.pending_manual_row_value_type,
            }
          : {}),
      };
    }
    if (prior?.pending_manual_row_baseline) {
      return {
        ...cell,
        raw_input: prior.raw_input,
        computed_value: prior.computed_value,
        kind: prior.kind,
        value_type: prior.value_type,
        optimistic_cell_id: prior.optimistic_cell_id,
        pending_manual_row_baseline: prior.pending_manual_row_baseline,
        manual_row_save_failed: prior.manual_row_save_failed,
        pending_manual_row_value_type: prior.pending_manual_row_value_type,
      };
    }
    return prior?.optimistic_cell_id
      ? { ...cell, optimistic_cell_id: prior.optimistic_cell_id }
      : cell;
  });
  return {
    ...incoming,
    sheet: {
      ...incoming.sheet,
      cells: [
        ...cells,
        ...pendingCells.filter((cell) => !matched.has(cell.id)),
      ],
    },
  };
};
